import { createContext, use, useMemo } from 'react';
import {
  useDismiss,
  useFloating,
  useInteractions,
  type UseInteractionsReturn,
  useRole,
} from '@floating-ui/react';
import { type ModalAction, type ModalOptions } from './types';

type UseModalReturn = {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  disableClose?: boolean;
  secondaryAction?: ModalAction;
  primaryAction?: ModalAction;
  fullScreen?: boolean;
  maxWidth?: string;
  minWidth?: string;
  constrainToViewport?: boolean;
} & ReturnType<typeof useFloating> &
  UseInteractionsReturn;

export const useModal = ({
  open,
  onOpenChange,
  onClose,
  secondaryAction,
  primaryAction,
  maxWidth,
  minWidth,
  fullScreen,
  constrainToViewport,
  disableClose,
  closeOnClickOutside,
  closeOnEscape = true,
}: ModalOptions): UseModalReturn => {
  const data = useFloating({
    open,
    onOpenChange,
  });

  const context = data.context;

  const dismiss = useDismiss(context, {
    outsidePressEvent: 'mousedown',
    outsidePress: closeOnClickOutside,
    escapeKey: closeOnEscape,
  });

  const role = useRole(context);

  const interactions = useInteractions([dismiss, role]);

  const val = useMemo(() => {
    const value: UseModalReturn = {
      open,
      ...interactions,
      ...data,
      onClose,
      onOpenChange,
      disableClose,
      secondaryAction,
      primaryAction,
      fullScreen,
      maxWidth,
      minWidth,
      constrainToViewport,
    };

    return value;
  }, [
    open,
    interactions,
    data,
    onClose,
    onOpenChange,
    disableClose,
    secondaryAction,
    primaryAction,
    fullScreen,
    maxWidth,
    minWidth,
    constrainToViewport,
  ]);

  return val;
};

type ContextType = ReturnType<typeof useModal> | null;

export const ModalContext = createContext<ContextType>(null);

export const useModalContext = () => {
  const context = use(ModalContext);

  if (context === null) {
    throw new Error('Modal components must be wrapped in <Modal />');
  }

  return context;
};
